import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type MediaAsset, type PublishedReel } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { MEDIA_THUMB_CLASS, MEDIA_THUMB_FRAME, MEDIA_THUMB_LIMIT_STYLE, MediaThumb } from "@/components/MediaThumb";
import { cn } from "@/lib/utils";

function fmt(sec: number) {
  const n = Math.max(0, Number(sec) || 0);
  const m = Math.floor(n / 60);
  const s = (n - m * 60).toFixed(1);
  return m ? `${m}:${s.padStart(4, "0")}` : `${Number(s).toFixed(1)}s`;
}

function matchesQuery(hay: string, q: string) {
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  return hay.toLowerCase().includes(needle);
}

export default function ReelClipModal({
  carouselId,
  indexN,
  apply = "one",
  protectFirstBg = true,
  fallback = false,
  onDone,
  onClose,
}: {
  carouselId: string;
  indexN: number;
  apply?: "one" | "all";
  protectFirstBg?: boolean;
  fallback?: boolean;
  onDone: () => void;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [tab, setTab] = useState<"library" | "graph">("graph");
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState<MediaAsset | null>(null);
  const [frameSec, setFrameSec] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [showClipRange, setShowClipRange] = useState(false);
  const [inSec, setInSec] = useState(0);
  const [outSec, setOutSec] = useState(3);
  const [scale, setScale] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);

  const listQ = useQuery({
    queryKey: ["media-reels", q],
    queryFn: () => api.mediaList({ kind: "video", q }),
  });
  const graphQ = useQuery({
    queryKey: ["media-reels-published"],
    queryFn: () => api.mediaReelsPublished({ limit: 60 }),
    enabled: tab === "graph",
  });

  const span = Math.max(0, outSec - inSec);
  const canVideo = span + 0.001 >= 3;
  const ffmpeg = listQ.data?.ffmpeg !== false;

  const syncFromVideo = (el: HTMLVideoElement) => {
    const t = Number.isFinite(el.currentTime) ? el.currentTime : 0;
    setFrameSec(t);
    if (Number.isFinite(el.duration) && el.duration > 0) setDuration(el.duration);
  };

  const seek = (t: number) => {
    const el = videoRef.current;
    const next = Math.max(0, Math.min(Number(t) || 0, duration || Number.MAX_SAFE_INTEGER));
    setFrameSec(next);
    if (el) {
      el.currentTime = next;
    }
  };

  const togglePlay = async () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.paused) {
      try {
        await el.play();
        setPlaying(true);
      } catch {
        setPlaying(false);
      }
    } else {
      el.pause();
      syncFromVideo(el);
      setPlaying(false);
    }
  };

  const pickAsset = (asset: MediaAsset) => {
    setPicked(asset);
    setPlaying(false);
    const dur = Number(asset.duration_sec || 0);
    setDuration(dur);
    setFrameSec(0);
    setInSec(0);
    setOutSec(dur >= 3 ? Math.min(dur, 5) : Math.max(dur, 0.2));
  };

  const uploadM = useMutation({
    mutationFn: (file: File) => api.mediaUpload(file),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["media-reels"] });
      if (res.asset) {
        setTab("library");
        pickAsset(res.asset);
      }
    },
  });
  const importM = useMutation({
    mutationFn: (id: string) => api.mediaReelImport(id),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["media-reels"] });
      qc.invalidateQueries({ queryKey: ["media-reels-published"] });
      if (res.asset) {
        setTab("library");
        pickAsset(res.asset);
      }
    },
  });
  const openPublished = async (row: PublishedReel) => {
    if (row.library_asset_id) {
      const asset = await api.mediaGet(row.library_asset_id);
      setTab("library");
      pickAsset(asset);
      return;
    }
    importM.mutate(row.id);
  };
  const clipM = useMutation({
    mutationFn: (payload: { mode: "still" | "video"; at?: number }) => {
      if (!picked) throw new Error("wybierz film");
      const mode = payload.mode;
      const at = mode === "still" ? Number(payload.at ?? frameSec) || 0 : inSec;
      const end = mode === "still" ? at + 0.05 : outSec;
      return api.mediaClip(picked.id, {
        in_sec: at,
        out_sec: end,
        mode,
        carousel_id: carouselId,
        index_n: indexN,
        apply,
        protect_first_bg: apply === "all" && protectFirstBg,
        fallback,
        scale,
        x: panX,
        y: panY,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carousel", carouselId] });
      onDone();
    },
  });

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !picked) return;
    el.pause();
    el.currentTime = 0;
    setPlaying(false);
  }, [picked?.id]);

  const published: PublishedReel[] = useMemo(() => {
    const rows = graphQ.data?.items || [];
    return rows.filter((row) => matchesQuery(`${row.caption || ""} ${row.id}`, q));
  }, [graphQ.data, q]);
  const reels = useMemo(() => listQ.data?.items || [], [listQ.data]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3">
      <div className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg border bg-background shadow-lg">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div>
            <h2 className="text-sm font-medium">Klatka z filmu IG jako tło</h2>
            <p className="text-[11px] text-muted-foreground">
              Wyszukaj film, odtwórz, zatrzymaj na kadrze i użyj tej klatki.
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={onClose}>
            Zamknij
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-2 border-b px-4 py-2">
          <button
            type="button"
            className={cn("rounded border px-2 py-0.5 text-[11px]", tab === "graph" ? "border-brand bg-brand/10" : "border-border")}
            onClick={() => setTab("graph")}
          >
            Filmiki IG
          </button>
          <button
            type="button"
            className={cn("rounded border px-2 py-0.5 text-[11px]", tab === "library" ? "border-brand bg-brand/10" : "border-border")}
            onClick={() => setTab("library")}
          >
            Biblioteka
          </button>
          <input
            className="min-w-[12rem] flex-1 rounded border px-2 py-1 text-xs"
            placeholder={tab === "graph" ? "szukaj w podpisie IG…" : "szukaj w bibliotece…"}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <label className="inline-flex h-7 cursor-pointer items-center rounded-md border border-input px-2 text-[11px]">
            Wgraj MP4
            <input
              type="file"
              accept="video/mp4,video/quicktime,.mp4,.mov"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) uploadM.mutate(file);
              }}
            />
          </label>
        </div>
        {!ffmpeg ? (
          <p className="px-4 pt-2 text-xs text-destructive">
            Na serwerze nie ma ffmpeg. Upload zadziała, wycięcie klatki nie. Doinstaluj ffmpeg.
          </p>
        ) : null}
        <div className="grid min-h-0 flex-1 gap-3 overflow-auto p-4 md:grid-cols-[1fr_1.1fr]">
          <div className="space-y-2">
            {tab === "library" ? (
              <>
                <div className="grid max-h-[28rem] grid-cols-2 gap-2 overflow-auto sm:grid-cols-3">
                  {reels.map((asset) => (
                    <button
                      key={asset.id}
                      type="button"
                      className={cn(
                        "overflow-hidden rounded border text-left",
                        picked?.id === asset.id ? "border-brand ring-1 ring-brand/40" : "border-border",
                      )}
                      onClick={() => pickAsset(asset)}
                    >
                      <MediaThumb asset={asset} />
                      <p className="truncate px-1.5 py-1 text-[11px]">{asset.original_name || asset.id.slice(0, 8)}</p>
                      <p className="px-1.5 pb-1 text-[10px] text-muted-foreground">
                        {asset.duration_sec ? fmt(asset.duration_sec) : "wideo"}
                        {asset.origin === "graph_reel" ? " · IG" : ""}
                      </p>
                    </button>
                  ))}
                </div>
                {!reels.length ? (
                  <p className="text-xs text-muted-foreground">
                    Pusto. Weź film z zakładki Filmiki IG albo wgraj MP4 / wrzuć do inbox/rolki.
                  </p>
                ) : null}
              </>
            ) : (
              <>
                {graphQ.data?.error ? <p className="text-xs text-destructive">{graphQ.data.error}</p> : null}
                <div className="grid max-h-[28rem] grid-cols-2 gap-2 overflow-auto sm:grid-cols-3">
                  {published.map((row) => (
                    <div
                      key={row.id}
                      className={cn(
                        "overflow-hidden rounded border",
                        row.library_asset_id && picked?.id === row.library_asset_id ? "border-brand" : "border-border",
                      )}
                    >
                      {row.thumbnail_url ? (
                        <span className={MEDIA_THUMB_FRAME} style={MEDIA_THUMB_LIMIT_STYLE}>
                          <img
                            src={row.thumbnail_url}
                            alt={row.caption || "Miniatura filmu IG"}
                            className={MEDIA_THUMB_CLASS}
                            style={{ ...MEDIA_THUMB_LIMIT_STYLE, width: "auto", height: "auto", objectFit: "contain" }}
                          />
                        </span>
                      ) : (
                        <div
                          className={cn(MEDIA_THUMB_FRAME, "text-[11px] text-muted-foreground")}
                          style={MEDIA_THUMB_LIMIT_STYLE}
                        >
                          brak miniatury
                        </div>
                      )}
                      <p className="line-clamp-2 px-1.5 py-1 text-[11px]">{row.caption || row.id}</p>
                      {row.library_asset_id || row.downloadable ? (
                        <Button
                          size="sm"
                          variant="outline"
                          className="m-1"
                          disabled={importM.isPending}
                          onClick={() => void openPublished(row)}
                        >
                          {row.library_asset_id ? "Otwórz" : "Pobierz i otwórz"}
                        </Button>
                      ) : (
                        <p className="px-1.5 pb-1 text-[10px] text-muted-foreground">{row.hint}</p>
                      )}
                    </div>
                  ))}
                </div>
                {graphQ.isLoading ? <p className="text-xs text-muted-foreground">Ładuję listę z IG…</p> : null}
                {!graphQ.isLoading && !published.length ? (
                  <p className="text-xs text-muted-foreground">Brak filmików albo filtr nic nie znalazł.</p>
                ) : null}
                {importM.error ? (
                  <p className="text-xs text-destructive">
                    {importM.error instanceof Error ? importM.error.message : "import nie wyszedł"}
                  </p>
                ) : null}
              </>
            )}
          </div>
          <div className="space-y-2">
            {picked ? (
              <>
                <video
                  ref={videoRef}
                  src={api.mediaFile(picked.id, false)}
                  className="aspect-[4/5] w-full rounded bg-black object-contain"
                  muted
                  playsInline
                  preload="metadata"
                  onLoadedMetadata={(e) => {
                    syncFromVideo(e.currentTarget);
                    const dur = e.currentTarget.duration;
                    if (dur && !picked.duration_sec) {
                      setOutSec(dur >= 3 ? Math.min(dur, 5) : dur);
                    }
                  }}
                  onTimeUpdate={(e) => {
                    if (!e.currentTarget.paused) setFrameSec(e.currentTarget.currentTime);
                  }}
                  onPlay={() => setPlaying(true)}
                  onPause={(e) => {
                    setPlaying(false);
                    syncFromVideo(e.currentTarget);
                  }}
                  onSeeked={(e) => syncFromVideo(e.currentTarget)}
                  onClick={() => void togglePlay()}
                />
                <div className="flex flex-wrap items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => void togglePlay()}>
                    {playing ? "Pauza" : "Odtwórz"}
                  </Button>
                  <span className="text-xs text-muted-foreground">
                    Klatka: <span className="font-medium text-foreground">{fmt(frameSec)}</span>
                    {duration ? ` / ${fmt(duration)}` : ""}
                  </span>
                </div>
                <label className="block text-[11px] text-muted-foreground">
                  Przewiń do klatki
                  <input
                    type="range"
                    min={0}
                    max={Math.max(duration, frameSec, 0.1)}
                    step={0.05}
                    className="mt-1 w-full"
                    value={Math.min(frameSec, Math.max(duration, frameSec, 0.1))}
                    onChange={(e) => {
                      const next = Number(e.target.value);
                      seek(next);
                      setPlaying(false);
                      videoRef.current?.pause();
                    }}
                  />
                </label>
                <p className="text-[11px] text-muted-foreground">
                  Zatrzymaj film na interesującym kadrze (klik w wideo albo Pauza), potem użyj tej klatki.
                </p>
                <Button
                  size="sm"
                  variant="brand"
                  className="w-full sm:w-auto"
                  disabled={clipM.isPending || !ffmpeg}
                  onClick={() => {
                    const el = videoRef.current;
                    let at = frameSec;
                    if (el) {
                      el.pause();
                      at = Number.isFinite(el.currentTime) ? el.currentTime : frameSec;
                      setFrameSec(at);
                      setPlaying(false);
                    }
                    clipM.mutate({ mode: "still", at });
                  }}
                >
                  {clipM.isPending ? "Wycinam klatkę…" : "Użyj tę klatkę jako tło"}
                </Button>
                <details
                  className="rounded-md border border-border bg-muted/20 p-2"
                  open={showClipRange}
                  onToggle={(e) => setShowClipRange(e.currentTarget.open)}
                >
                  <summary className="cursor-pointer text-xs font-medium">Zaawansowane: klip wideo / przybliżenie</summary>
                  <div className="mt-2 space-y-2">
                    <label className="block text-[11px] text-muted-foreground">
                      Od {fmt(inSec)}
                      <input
                        type="range"
                        min={0}
                        max={Math.max(duration, outSec, 0.1)}
                        step={0.1}
                        className="mt-1 w-full"
                        value={inSec}
                        onChange={(e) => {
                          const next = Number(e.target.value);
                          setInSec(next);
                          if (next >= outSec) setOutSec(next + 0.2);
                          seek(next);
                        }}
                      />
                    </label>
                    <label className="block text-[11px] text-muted-foreground">
                      Do {fmt(outSec)}
                      <input
                        type="range"
                        min={0}
                        max={Math.max(duration, outSec, inSec + 0.2)}
                        step={0.1}
                        className="mt-1 w-full"
                        value={outSec}
                        onChange={(e) => {
                          const next = Number(e.target.value);
                          setOutSec(next);
                          seek(next);
                        }}
                      />
                    </label>
                    <p className="text-[11px] text-muted-foreground">
                      Fragment: {fmt(span)}. Slajd wideo na IG wymaga min. 3 s.
                    </p>
                    <label className="block text-[11px] text-muted-foreground">
                      Zbliżenie {scale.toFixed(2)}×
                      <input
                        type="range"
                        min={1}
                        max={2.5}
                        step={0.05}
                        className="mt-1 w-full"
                        value={scale}
                        onChange={(e) => setScale(Number(e.target.value))}
                      />
                    </label>
                    <label className="block text-[11px] text-muted-foreground">
                      Pozycja X {panX}
                      <input
                        type="range"
                        min={-400}
                        max={400}
                        step={10}
                        className="mt-1 w-full"
                        value={panX}
                        onChange={(e) => setPanX(Number(e.target.value))}
                      />
                    </label>
                    <label className="block text-[11px] text-muted-foreground">
                      Pozycja Y {panY}
                      <input
                        type="range"
                        min={-400}
                        max={400}
                        step={10}
                        className="mt-1 w-full"
                        value={panY}
                        onChange={(e) => setPanY(Number(e.target.value))}
                      />
                    </label>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={clipM.isPending || !ffmpeg || !canVideo}
                      onClick={() => clipM.mutate({ mode: "video" })}
                    >
                      Slajd wideo z zakresu
                    </Button>
                  </div>
                </details>
              </>
            ) : (
              <p className="text-xs text-muted-foreground">
                Wybierz filmik z lewej (IG albo biblioteka). Potem odtwórz, pauza, użyj klatki.
              </p>
            )}
            {clipM.error ? (
              <p className="text-xs text-destructive">
                {clipM.error instanceof Error ? clipM.error.message : "cięcie nie wyszło"}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
